import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { landing as landingApi } from '@/lib/api';
import PublicNav, { PublicFooter } from '@/components/PublicNav';
import { ArrowRight, Database, Brain, Radio, HardDrive, Cpu, Clock, DollarSign, Gauge, Server, Search, GitBranch, Workflow, Box, FileText } from 'lucide-react';

const serviceDetails = {
  bge_m3: {
    icon: Brain,
    color: 'from-violet-500 to-purple-600',
    accent: 'violet',
    useCases: ['Semantic search', 'Document retrieval', 'RAG pipelines', 'Multilingual similarity'],
    details: 'Generates dense (1024d), sparse, and ColBERT embeddings in a single forward pass. Supports 100+ languages. Perfect for hybrid search combining keyword and semantic matching.'
  },
  bge_reranker: {
    icon: Workflow,
    color: 'from-blue-500 to-indigo-600',
    accent: 'blue',
    useCases: ['Search reranking', 'Relevance scoring', 'Two-stage retrieval', 'Document ranking'],
    details: 'Cross-encoder reranker that takes query-passage pairs and outputs fine-grained relevance scores. Use it as the second stage after initial retrieval to dramatically improve precision.'
  },
  minilm: {
    icon: Cpu,
    color: 'from-teal-500 to-emerald-600',
    accent: 'teal',
    useCases: ['Real-time classification', 'Lightweight embeddings', 'Edge inference', 'Clustering'],
    details: 'Produces 384-dimensional sentence embeddings with sub-millisecond latency. Ideal when speed matters more than dimensionality. 6x faster than BGE-M3.'
  },
  spacy: {
    icon: FileText,
    color: 'from-amber-500 to-orange-600',
    accent: 'amber',
    useCases: ['Named entity recognition', 'POS tagging', 'Dependency parsing', 'Text preprocessing'],
    details: 'Full NLP pipeline returning entities, tokens with POS tags, noun chunks, and sentence boundaries. Built on spaCy en_core_web_sm, GPU-accelerated for batch processing.'
  },
  qdrant: {
    icon: Search,
    color: 'from-rose-500 to-pink-600',
    accent: 'rose',
    useCases: ['Vector similarity search', 'Recommendation engines', 'Image search', 'Anomaly detection'],
    details: 'High-performance vector database supporting filtering, payload indexing, and multi-tenancy. Stores billions of vectors with sub-second query times. HNSW-based with quantization support.'
  },
  elasticsearch: {
    icon: Database,
    color: 'from-cyan-500 to-blue-600',
    accent: 'cyan',
    useCases: ['Full-text search', 'Log analytics', 'Autocomplete', 'Faceted search'],
    details: 'Distributed search and analytics engine. Supports BM25 ranking, aggregations, fuzzy matching, and custom analyzers. Perfect for structured + unstructured data queries at scale.'
  },
  neo4j: {
    icon: GitBranch,
    color: 'from-green-500 to-emerald-600',
    accent: 'green',
    useCases: ['Knowledge graphs', 'Social networks', 'Fraud detection', 'Dependency analysis'],
    details: 'Native graph database with Cypher query language. Traverse millions of relationships in milliseconds. Ideal for connected data where relationships are first-class citizens.'
  },
  redis: {
    icon: Gauge,
    color: 'from-red-500 to-rose-600',
    accent: 'red',
    useCases: ['Caching', 'Session storage', 'Real-time leaderboards', 'Pub/Sub messaging'],
    details: 'In-memory data store with sub-millisecond reads. Supports strings, hashes, lists, sets, sorted sets, streams, and pub/sub. The fastest cache layer for your AI pipeline.'
  },
  kafka: {
    icon: Radio,
    color: 'from-orange-500 to-red-600',
    accent: 'orange',
    useCases: ['Event streaming', 'Log aggregation', 'Change data capture', 'Microservice communication'],
    details: 'Distributed event streaming platform handling millions of events per second. Durable, fault-tolerant, and ordered. Build real-time data pipelines and streaming applications.'
  },
  minio: {
    icon: Box,
    color: 'from-slate-500 to-gray-700',
    accent: 'slate',
    useCases: ['Model storage', 'Dataset management', 'Backup and archival', 'Static asset hosting'],
    details: 'S3-compatible object storage. Store models, training data, embeddings, and artifacts. Supports versioning, lifecycle policies, and multi-part uploads. Drop-in replacement for AWS S3.'
  },
};

export default function ServicesPage() {
  const [content, setContent] = useState(null);
  const [pricingData, setPricingData] = useState(null);

  useEffect(() => {
    landingApi.get().then(r => setContent(r.data)).catch(() => {});
    landingApi.pricing().then(r => setPricingData(r.data)).catch(() => {});
  }, []);

  if (!content || !pricingData) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const svcSection = content.services_section || {};
  const services = pricingData?.services || {};
  const footer = content.footer || {};

  const categories = { ml_model: { label: 'ML Models', icon: Brain }, database: { label: 'Databases', icon: Database }, streaming: { label: 'Streaming', icon: Radio }, storage: { label: 'Storage', icon: HardDrive } };
  const grouped = {};
  Object.entries(services).forEach(([id, svc]) => {
    if (!grouped[svc.category]) grouped[svc.category] = [];
    grouped[svc.category].push({ ...svc, id });
  });

  return (
    <div data-testid="services-info-page" className="light-page min-h-screen bg-white text-slate-900">
      <PublicNav />

      {/* Hero */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-20 px-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-1/3 w-[500px] h-[500px] bg-indigo-50 rounded-full blur-[120px] opacity-60" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-violet-50 rounded-full blur-[100px] opacity-50" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h1 data-testid="services-hero-title" className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-slate-900 mb-5" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
            {svcSection.title || 'Every Tool Your AI Stack Needs'}
          </h1>
          <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
            {svcSection.subtitle || 'GPU-accelerated services behind one API key.'}
          </p>
        </div>
      </section>

      {/* Category sections with detailed service cards */}
      {Object.entries(grouped).map(([catKey, svcs]) => {
        const cat = categories[catKey] || { label: catKey, icon: Cpu };
        const CatIcon = cat.icon;
        return (
          <section key={catKey} className="py-14 px-5 even:bg-slate-50/70">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <CatIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>{cat.label}</h2>
              </div>
              <div className="space-y-6">
                {svcs.map(svc => {
                  const det = serviceDetails[svc.id] || {};
                  const Icon = det.icon || Cpu;
                  return (
                    <div key={svc.id} data-testid={`service-detail-${svc.id}`} className="rounded-2xl border border-slate-100 bg-white p-6 md:p-8 hover:shadow-lg hover:shadow-slate-100 transition-all duration-300">
                      <div className="flex flex-col lg:flex-row gap-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${det.color || 'from-indigo-500 to-violet-600'} flex items-center justify-center shadow-sm`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-slate-900" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>{svc.name}</h3>
                              <p className="text-sm text-slate-500">{svc.description}</p>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 leading-relaxed mb-4">
                            {det.details || svc.description}
                          </p>
                          {det.useCases && (
                            <div className="flex flex-wrap gap-2">
                              {det.useCases.map((uc, i) => (
                                <span key={i} className="px-3 py-1 rounded-full bg-slate-50 border border-slate-100 text-xs font-medium text-slate-600">{uc}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Tier pricing sidebar */}
                        <div className="lg:w-72 flex flex-col gap-2">
                          {['economy', 'standard', 'priority'].map(tier => {
                            const p = svc.pricing?.[tier];
                            if (!p) return null;
                            const tierStyles = {
                              economy: { label: 'Economy', bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600' },
                              standard: { label: 'Standard', bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700' },
                              priority: { label: 'Priority', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
                            };
                            const ts = tierStyles[tier];
                            return (
                              <div key={tier} className={`flex items-center justify-between px-4 py-3 rounded-xl ${ts.bg} border ${ts.border}`}>
                                <div>
                                  <p className={`text-xs font-bold uppercase tracking-wider ${ts.text}`}>{ts.label}</p>
                                  <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {'\u2264'}{p.timeout_seconds}s response</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-base font-bold text-slate-900 font-mono">${p.hourly_rate}</p>
                                  <p className="text-[11px] text-slate-500">/hour</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA to pricing */}
      <section className="py-20 px-5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-slate-900 mb-4" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>Ready to start building?</h2>
          <p className="text-slate-500 text-lg mb-8">Choose a plan that fits your workload. Start free, upgrade anytime.</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white text-base font-semibold shadow-lg shadow-indigo-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
              View Pricing <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/signup" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl border border-slate-200 text-slate-700 text-base font-semibold hover:bg-slate-50 transition-all">
              Create Free Account
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter footer={footer} />
    </div>
  );
}
